import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { FaTimes } from 'react-icons/fa';

import CustomLink from '@/components/common/CustomLink/CustomLink';
import Modal from '@/components/common/Modals/Modal';
import {
  DisplayFrequency,
  ModalSizeUnit,
  PageAlertsBlock,
  PageBlockStyles,
  RouterType,
} from '@/components/common/Providers/GraphqlProvider/generated';
import { Button } from '@/components/ui/Button';

interface AlertBlockProps {
  block: PageAlertsBlock;
}

interface AlertContentProps {
  block: PageAlertsBlock;
  onClose?: () => void;
}

interface AlertErrorProps {
  onClose?: () => void;
  title: string;
  message: string;
  type?: 'error' | 'warning';
}

// Componente reutilizable para mostrar errores de configuración
function AlertError({ onClose = () => {}, title, message, type = 'error' }: AlertErrorProps) {
  const isError = type === 'error';
  const bgColor = isError ? 'bg-red-50' : 'bg-yellow-50';
  const textColor = isError ? 'text-red-800' : 'text-yellow-800';

  return (
    <div className="flex items-center justify-center p-8 text-center">
      <button
        onClick={onClose}
        className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg transition-colors hover:bg-gray-100 focus:outline-none"
        aria-label="Cerrar modal"
      >
        <FaTimes className="size-5 text-primary" />
      </button>
      <div className={`rounded-lg ${bgColor} p-6 ${textColor}`}>
        <h3 className="mb-2 text-lg font-semibold">{title}</h3>
        <p>{message}</p>
      </div>
    </div>
  );
}

// Componente del contenido de la alerta (separado para previsualización)
export function AlertContent({ block, onClose = () => {} }: AlertContentProps) {
  if (!block) {
    return (
      <AlertError
        onClose={onClose}
        title="Error de configuración"
        message="Falta configuración en el bloque de alerta"
        type="error"
      />
    );
  }

  // Si existe htmlTemplate, renderizar el HTML personalizado
  if (block.htmlTemplate) {
    return (
      <div className="relative">
        {/* Botón de cerrar flotante */}
        <button
          onClick={onClose}
          className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg transition-colors hover:bg-gray-100 focus:outline-none"
          aria-label="Cerrar modal"
        >
          <FaTimes className="size-5 text-primary" />
        </button>

        {/* Contenido HTML personalizado */}
        <div
          className="relative flex justify-center overflow-hidden rounded-lg shadow-xl"
          style={{
            width: '100%',
            height: block.modalHeight
              ? `${block.modalHeight}${block.modalSizeUnit === ModalSizeUnit.Px ? 'px' : 'vh'}`
              : 'auto',
            maxHeight: '96vh',
          }}
        >
          <div className="relative overflow-y-auto p-5">
            <div dangerouslySetInnerHTML={{ __html: block.htmlTemplate }} />
          </div>
        </div>
      </div>
    );
  }

  // Si no hay htmlTemplate pero tampoco imagen, mostrar mensaje de error
  if (!block.image) {
    return (
      <AlertError
        onClose={onClose}
        title="Configuración incompleta"
        message="Este bloque de alerta necesita una imagen o contenido HTML para mostrarse correctamente"
        type="warning"
      />
    );
  }

  const isStyleA = block.alertStyle === PageBlockStyles.A;
  const isStyleB = block.alertStyle === PageBlockStyles.B;

  return (
    <div className="relative">
      {/* Botón de cerrar flotante */}
      <button
        onClick={onClose}
        className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg transition-colors hover:bg-gray-100 focus:outline-none"
        aria-label="Cerrar modal"
      >
        <FaTimes className="size-5 text-primary" />
      </button>

      {/* Contenido según el estilo */}
      {isStyleA && block.image && (
        // Estilo A: Solo mostrar la imagen
        <div className="relative overflow-hidden rounded-lg shadow-xl">
          <div className="relative h-[70vh] w-full">
            {block.linkItem && block.linkItem.routeType !== RouterType.NoAction ? (
              <CustomLink pageLink={block.linkItem}>
                <Image
                  fill
                  alt={block.title || 'Alerta'}
                  src={block.image.downloadUrl || ''}
                  className="cursor-pointer object-cover transition-opacity"
                />
              </CustomLink>
            ) : (
              <Image
                alt={block.title || ''}
                src={block.image.downloadUrl || ''}
                fill
                className="object-cover"
              />
            )}
          </div>
        </div>
      )}

      {isStyleB && block.image && (
        // Estilo B: Imagen de fondo con panel de información
        <div className="relative overflow-hidden rounded-lg shadow-xl">
          {/* Imagen de fondo */}
          <div className="relative h-[70vh] w-full">
            <Image
              src={block.image.downloadUrl || ''}
              alt={block.title || 'Alerta'}
              fill
              className="object-cover"
            />

            {/* Panel blanco con información */}
            <div className="absolute inset-x-0 bottom-0 bg-white px-10 py-5 shadow-lg">
              {block.showTitle && (
                <h2 className="mb-3 text-3xl font-semibold text-primary">{block.title}</h2>
              )}

              {block.showDescription && block.alertDescription && (
                <p className="font-atlassianSans text-base font-normal text-tundora">
                  {block.alertDescription}
                </p>
              )}

              {/* Botón de acción si existe linkItem */}
              {block.linkItem && block.linkItem.routeType !== RouterType.NoAction && (
                <div className="mt-3">
                  <CustomLink pageLink={block.linkItem}>
                    <Button className="rounded-full px-3 py-2.5 font-atlassianSans text-sm font-medium">
                      {block.buttonTitle || 'Ir ahora'}
                    </Button>
                  </CustomLink>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AlertBlock({ block }: AlertBlockProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (!block?.id) return;

    const storageKey = `alert-${block.id}-shown`;
    const hasBeenShown = localStorage.getItem(storageKey);

    if (block.displayFrequency === DisplayFrequency.Once) {
      if (!hasBeenShown) {
        setIsModalOpen(true);
        localStorage.setItem(storageKey, 'true');
      }
    } else {
      localStorage.removeItem(storageKey);
      setIsModalOpen(true);
    }
  }, [block?.id, block?.displayFrequency]);

  const isImageSelected = useMemo(() => {
    // Determinar el contentType basado en la información disponible
    let contentType = 'image'; // valor por defecto
    if (block.htmlTemplate && block.htmlTemplate.trim() !== '') {
      contentType = 'html';
    } else if (block.image) {
      contentType = 'image';
    }
    return contentType;
  }, [block]);

  if (!block) return <></>;

  if (isImageSelected === 'image') {
    return (
      <>
        <Modal open={isModalOpen} handler={closeModal} size="xl">
          <AlertContent block={block} onClose={closeModal} />
        </Modal>
      </>
    );
  }

  return (
    <>
      <Modal
        open={isModalOpen}
        handler={closeModal}
        className="min-w-fit"
        style={{
          width: block.modalWidth
            ? `${block.modalWidth}${block.modalSizeUnit === ModalSizeUnit.Px ? 'px' : '%'}`
            : 'auto',
          minWidth:
            block.modalWidth && block.modalSizeUnit === ModalSizeUnit.Px
              ? `${block.modalWidth}px`
              : undefined,
          maxWidth: '98%',
        }}
      >
        <AlertContent block={block} onClose={closeModal} />
      </Modal>
    </>
  );
}

export function AlertBlockSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 w-full rounded bg-gray-200" />
    </div>
  );
}
